import React, { useRef, useEffect, useState, useCallback } from 'react';
import Header from '../components/Header';
import SegmentEditorPanel from '../components/SegmentEditorPanel';
import { authHeaders } from '../utils/authService';
import {
  TRACK_COLORS,
  TRACK_DEFAULTS,
  parseSegments,
  computeScreenPoints,
  drawTrack,
  findNearestPoint,
  drawEditOverlay,
} from '../utils/trackRenderer';


export default function TrackViewerPage({ track, onBack, user, onLogout, onAdmin, onDashboard, onProfile, isAdmin }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1, rotation: 0 });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const lastTransformRef = useRef(transform);

  useEffect(() => {
    lastTransformRef.current = transform;
  }, [transform]);

  const [fetchedCoordinates, setFetchedCoordinates] = useState(null);

  useEffect(() => {
    const trackId = track?.trackId;
    if (!trackId || (track?.coordinates && track.coordinates.length > 0)) return;

    fetch(`/api/tracks/${trackId}/coordinates`, { headers: authHeaders() })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.coordinates) setFetchedCoordinates(data.coordinates);
      })
      .catch(() => {});
  }, [track]);

  const baseCoordinates = React.useMemo(
    () => fetchedCoordinates || track?.coordinates || [],
    [fetchedCoordinates, track]
  );

  // ── Edit mode state ──
  const [editMode, setEditMode] = useState(false);
  const [editCoordinates, setEditCoordinates] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // { type: 'success'|'error', msg }
  const [selectedSegIdx, setSelectedSegIdx] = useState(-1);
  const [hoveredPointIdx, setHoveredPointIdx] = useState(-1);
  const [selectedPointIdx, setSelectedPointIdx] = useState(-1);
  const [editAction, setEditAction] = useState(null);
  const [editTargetSegIdx, setEditTargetSegIdx] = useState(-1);
  const [pendingStartIdx, setPendingStartIdx] = useState(-1);

  const screenPtsRef = useRef([]);

  const coordinates = editMode && editCoordinates ? editCoordinates : baseCoordinates;
  const segments = React.useMemo(() => parseSegments(coordinates), [coordinates]);

  // Clear save status after 3s
  useEffect(() => {
    if (saveStatus?.type === 'success') {
      const t = setTimeout(() => setSaveStatus(null), 3000);
      return () => clearTimeout(t);
    }
  }, [saveStatus]);

  // ── Enter/exit edit mode ──
  const enterEditMode = () => {
    const trackId = track?.trackId;
    if (!trackId) return;
    setEditCoordinates(JSON.parse(JSON.stringify(baseCoordinates)));
    setEditMode(true);
    setIsDirty(false);
    setSaveStatus(null);
    clearSelection();
  };

  const exitEditMode = async () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setEditMode(false);
    setEditCoordinates(null);
    setIsDirty(false);
    clearSelection();
    // Reload track from server so view reflects saved coordinates
    const trackId = track?.trackId;
    if (trackId) {
      try {
        const res = await fetch(`/api/tracks/${trackId}/coordinates`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data?.coordinates) setFetchedCoordinates(data.coordinates);
        }
      } catch (_) { /* keep current data on failure */ }
    }
  };

  const clearSelection = () => {
    setSelectedSegIdx(-1);
    setHoveredPointIdx(-1);
    setSelectedPointIdx(-1);
    setEditAction(null);
    setEditTargetSegIdx(-1);
    setPendingStartIdx(-1);
  };

  const handleBack = () => {
    if (editMode && isDirty) {
      if (!window.confirm('You have unsaved changes. Discard them?')) return;
    }
    onBack();
  };

  // ── Segment editing operations ──
  const updateCoord = (idx, changes) => {
    setEditCoordinates((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...changes };
      return next;
    });
    setIsDirty(true);
  };

  const clearCommand = (idx) => {
    setEditCoordinates((prev) => {
      const next = [...prev];
      const { command, label, ...rest } = next[idx];
      next[idx] = rest;
      return next;
    });
    setIsDirty(true);
  };

  const handleDeleteSegment = (segIdx) => {
    const seg = segments[segIdx];
    if (!seg) return;
    clearCommand(seg.startIdx);
    clearCommand(seg.endIdx);
    if (selectedSegIdx === segIdx) setSelectedSegIdx(-1);
  };

  const handleRenameSegment = (segIdx, newLabel) => {
    const seg = segments[segIdx];
    if (!seg) return;
    updateCoord(seg.startIdx, { label: newLabel });
  };

  const handleMoveStart = (segIdx) => {
    setEditAction('pickStart');
    setEditTargetSegIdx(segIdx);
    setSelectedSegIdx(segIdx);
  };

  const handleMoveEnd = (segIdx) => {
    setEditAction('pickEnd');
    setEditTargetSegIdx(segIdx);
    setSelectedSegIdx(segIdx);
  };

  const handleCreateNew = () => {
    setEditAction('createStart');
    setPendingStartIdx(-1);
    setSelectedSegIdx(-1);
  };

  const handleCancelAction = () => {
    setEditAction(null);
    setEditTargetSegIdx(-1);
    setPendingStartIdx(-1);
  };

  const handlePointClick = (pointIdx) => {
    if (pointIdx < 0) return;

    if (editAction === 'pickStart') {
      const seg = segments[editTargetSegIdx];
      if (!seg) { handleCancelAction(); return; }
      if (pointIdx >= seg.endIdx) {
        alert('Start point must be before the end point.');
        return;
      }
      // Check for existing command on target
      if (editCoordinates[pointIdx].command && pointIdx !== seg.startIdx) {
        if (!window.confirm(`Point ${pointIdx} already has a "${editCoordinates[pointIdx].command}" marker. Overwrite it?`)) return;
      }
      const label = editCoordinates[seg.startIdx].label || '';
      clearCommand(seg.startIdx);
      updateCoord(pointIdx, { command: 'start', label });
      handleCancelAction();
      return;
    }

    if (editAction === 'pickEnd') {
      const seg = segments[editTargetSegIdx];
      if (!seg) { handleCancelAction(); return; }
      if (pointIdx <= seg.startIdx) {
        alert('End point must be after the start point.');
        return;
      }
      if (editCoordinates[pointIdx].command && pointIdx !== seg.endIdx) {
        if (!window.confirm(`Point ${pointIdx} already has a "${editCoordinates[pointIdx].command}" marker. Overwrite it?`)) return;
      }
      clearCommand(seg.endIdx);
      updateCoord(pointIdx, { command: 'stop' });
      handleCancelAction();
      return;
    }

    if (editAction === 'createStart') {
      if (editCoordinates[pointIdx].command) {
        if (!window.confirm(`Point ${pointIdx} already has a "${editCoordinates[pointIdx].command}" marker. Overwrite it?`)) return;
      }
      setPendingStartIdx(pointIdx);
      setEditAction('createEnd');
      setSelectedPointIdx(pointIdx);
      return;
    }

    if (editAction === 'createEnd') {
      if (pointIdx === pendingStartIdx) return;
      const startIdx = Math.min(pendingStartIdx, pointIdx);
      const endIdx = Math.max(pendingStartIdx, pointIdx);
      if (editCoordinates[pointIdx].command) {
        if (!window.confirm(`Point ${pointIdx} already has a "${editCoordinates[pointIdx].command}" marker. Overwrite it?`)) return;
      }
      const label = prompt('Segment label (e.g. "Turn 1"):') || '';
      updateCoord(startIdx, { command: 'start', label });
      updateCoord(endIdx, { command: 'stop' });
      handleCancelAction();
      setSelectedPointIdx(-1);
      return;
    }

    // No action — just select
    setSelectedPointIdx(pointIdx);
    // If this point is a segment boundary, select that segment
    const segMatch = segments.findIndex(
      (s) => s.startIdx === pointIdx || s.endIdx === pointIdx
    );
    setSelectedSegIdx(segMatch >= 0 ? segMatch : -1);
  };

  // ── Save / Discard ──
  const handleSave = async () => {
    if (!editCoordinates) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      const res = await fetch(`/api/tracks/${track.trackId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ coordinates: editCoordinates }),
      });
      if (!res.ok) throw new Error('Server error');
      setIsDirty(false);
      setSaveStatus({ type: 'success', msg: 'Saved' });
    } catch (err) {
      setSaveStatus({ type: 'error', msg: 'Save failed: ' + err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!window.confirm('Discard all changes?')) return;
    setEditCoordinates(JSON.parse(JSON.stringify(baseCoordinates)));
    setIsDirty(false);
    clearSelection();
    setSaveStatus(null);
  };

  // ── Drawing ──
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || coordinates.length === 0) return;

    const ctx = canvas.getContext('2d');
    const tf = lastTransformRef.current;

    const screenPts = computeScreenPoints(
      coordinates, canvas.width, canvas.height, TRACK_DEFAULTS.padding, tf,
      !!track?.metadata?.mirrorX, null, !!track?.metadata?.mirrorY
    );
    screenPtsRef.current = screenPts;

    drawTrack(ctx, screenPts, segments, tf.scale);

    if (editMode) {
      drawEditOverlay(ctx, screenPts, coordinates, {
        hoveredIdx: hoveredPointIdx,
        selectedIdx: selectedPointIdx,
        pendingStartIdx: editAction === 'createEnd' ? pendingStartIdx : -1,
        zoom: tf.scale,
      });
    }

    // Track info overlay (top-left)
    const infoFontSize = 14;
    ctx.font = `bold ${infoFontSize}px "JetBrains Mono", "Fira Code", monospace`;
    ctx.fillStyle = TRACK_COLORS.labelBg;
    const trackLabel = track.trackName || 'Track';
    const segLabel = `${segments.length} segment${segments.length !== 1 ? 's' : ''}`;
    const infoText = `${trackLabel}  |  ${coordinates.length} pts  |  ${segLabel}`;
    const infoW = ctx.measureText(infoText).width;
    ctx.fillRect(12, 12, infoW + 20, infoFontSize + 16);
    ctx.fillStyle = TRACK_COLORS.labelText;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(infoText, 22, 20);

    // Point info tooltip when hovering in edit mode
    if (editMode && hoveredPointIdx >= 0 && hoveredPointIdx < coordinates.length) {
      const pt = coordinates[hoveredPointIdx];
      const sp = screenPts[hoveredPointIdx];
      if (sp) {
        const tipFontSize = 11;
        ctx.font = `${tipFontSize}px "JetBrains Mono", monospace`;
        let tipText = `#${hoveredPointIdx}`;
        if (pt.command) tipText += ` [${pt.command}${pt.label ? ': ' + pt.label : ''}]`;
        const tw = ctx.measureText(tipText).width;
        const tx = sp.x + 14;
        const ty = sp.y - 24;
        ctx.fillStyle = 'rgba(0,0,0,0.85)';
        ctx.beginPath();
        ctx.roundRect(tx - 4, ty - 2, tw + 8, tipFontSize + 6, 3);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(tipText, tx, ty);
      }
    }
  }, [coordinates, segments, track, editMode, hoveredPointIdx, selectedPointIdx, editAction, pendingStartIdx]);

  // ── Canvas sizing ──
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      draw();
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [draw]);

  useEffect(() => {
    draw();
  }, [transform, draw]);

  // ── Mouse/touch gestures ──
  const handleMouseDown = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      canvasX: e.clientX - rect.left,
      canvasY: e.clientY - rect.top,
      startTf: { ...lastTransformRef.current },
      didDrag: false,
    };
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();

    // Hover detection in edit mode
    if (editMode && rect && screenPtsRef.current.length > 0) {
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const nearest = findNearestPoint(screenPtsRef.current, mx, my, 20);
      if (nearest !== hoveredPointIdx) {
        setHoveredPointIdx(nearest);
      }
    }

    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragRef.current.didDrag = true;
    }
    setTransform({
      ...dragRef.current.startTf,
      x: dragRef.current.startTf.x + dx,
      y: dragRef.current.startTf.y + dy,
    });
  };

  const handleMouseUp = () => {
    if (dragRef.current && !dragRef.current.didDrag && editMode) {
      // This was a click, not a drag
      const mx = dragRef.current.canvasX;
      const my = dragRef.current.canvasY;
      const nearest = findNearestPoint(screenPtsRef.current, mx, my, 20);
      if (nearest >= 0) {
        handlePointClick(nearest);
      }
    }
    dragRef.current = null;
  };

  const handleMouseLeave = () => {
    dragRef.current = null;
    if (editMode) setHoveredPointIdx(-1);
  };

  const handleWheel = (e) => {
    if (!e.ctrlKey && !e.shiftKey) return;
    e.preventDefault();
    if (e.ctrlKey) {
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setTransform((prev) => ({
        ...prev,
        scale: Math.max(0.1, Math.min(20, prev.scale * delta)),
      }));
    } else if (e.shiftKey) {
      const angleDelta = e.deltaY > 0 ? 0.05 : -0.05;
      setTransform((prev) => ({
        ...prev,
        rotation: prev.rotation + angleDelta,
      }));
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      dragRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startTf: { ...lastTransformRef.current },
        didDrag: false,
      };
    } else if (e.touches.length === 2) {
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      pinchRef.current = {
        startDist: Math.sqrt(dx * dx + dy * dy),
        startAngle: Math.atan2(dy, dx),
        startTf: { ...lastTransformRef.current },
      };
      dragRef.current = null;
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && dragRef.current) {
      const dx = e.touches[0].clientX - dragRef.current.startX;
      const dy = e.touches[0].clientY - dragRef.current.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.didDrag = true;
      setTransform({
        ...dragRef.current.startTf,
        x: dragRef.current.startTf.x + dx,
        y: dragRef.current.startTf.y + dy,
      });
    } else if (e.touches.length === 2 && pinchRef.current) {
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);
      const scaleRatio = dist / pinchRef.current.startDist;
      const angleDelta = angle - pinchRef.current.startAngle;
      setTransform({
        ...pinchRef.current.startTf,
        scale: Math.max(0.1, Math.min(20, pinchRef.current.startTf.scale * scaleRatio)),
        rotation: pinchRef.current.startTf.rotation + angleDelta,
      });
    }
  };

  const handleTouchEnd = () => {
    dragRef.current = null;
    pinchRef.current = null;
  };

  const resetView = () => {
    setTransform({ x: 0, y: 0, scale: 1, rotation: 0 });
  };

  return (
    <div style={pageStyles.page} data-doc-id="track-viewer-page">
      <Header user={user} onLogout={onLogout} onBack={handleBack} showBack onAdmin={onAdmin} onDashboard={onDashboard} onProfile={onProfile} />
      <div style={pageStyles.toolbar} data-doc-id="track-viewer-toolbar">
        <span style={pageStyles.toolbarTitle}>TRACK MAP</span>
        <span style={pageStyles.trackNameLabel}>{track?.trackName || 'Unknown Track'}</span>
        <span style={pageStyles.segCount}>
          {segments.length} segment{segments.length !== 1 ? 's' : ''}
          {' | '}
          {coordinates.length} points
        </span>
        {saveStatus && (
          <span style={{
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            fontWeight: 600,
            color: saveStatus.type === 'success' ? '#2ECC71' : '#E74C3C',
          }}>
            {saveStatus.msg}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <button
            data-doc-id="track-editor-toggle"
            style={{
              ...pageStyles.resetBtn,
              ...(editMode ? { background: 'var(--color-primary)', color: '#fff', borderColor: 'var(--color-primary)' } : {}),
            }}
            onClick={editMode ? exitEditMode : enterEditMode}
          >
            {editMode ? 'Exit Editor' : 'Edit Segments'}
          </button>
        )}
        <button style={pageStyles.resetBtn} onClick={resetView} data-doc-id="track-view-reset">Reset View</button>
      </div>
      <div style={pageStyles.body}>
        <div ref={containerRef} style={pageStyles.canvasContainer} data-doc-id="track-viewer-canvas-wrap">
          {coordinates.length === 0 ? (
            <div style={pageStyles.emptyState}>
              <div style={pageStyles.emptyTitle}>No GPS Data</div>
              <div style={pageStyles.emptyText}>This track has no coordinate data available.</div>
            </div>
          ) : (
            <canvas
              ref={canvasRef}
              data-doc-id="track-viewer-canvas"
              style={{
                ...pageStyles.canvas,
                cursor: editMode ? (editAction ? 'crosshair' : 'default') : 'grab',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          )}
        </div>
        {editMode && (
          <SegmentEditorPanel
            segments={segments}
            coordinates={coordinates}
            selectedSegIdx={selectedSegIdx}
            editAction={editAction}
            isDirty={isDirty}
            saving={saving}
            onSelectSegment={(i) => {
              setSelectedSegIdx(i);
              if (segments[i]) setSelectedPointIdx(segments[i].startIdx);
            }}
            onDeleteSegment={handleDeleteSegment}
            onRenameSegment={handleRenameSegment}
            onMoveStart={handleMoveStart}
            onMoveEnd={handleMoveEnd}
            onCreateNew={handleCreateNew}
            onCancelAction={handleCancelAction}
            onSave={handleSave}
            onDiscard={handleDiscard}
          />
        )}
      </div>
      <div style={pageStyles.bottomBar}>
        <span style={pageStyles.hint}>
          Drag to pan &bull; Ctrl+Scroll to zoom &bull; Shift+Scroll to rotate
          {editMode && ' &bull; Click points to select/edit segments'}
        </span>
      </div>
    </div>
  );
}

const pageStyles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: TRACK_COLORS.background,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '10px 24px',
    background: 'var(--color-surface)',
    borderBottom: '1px solid var(--color-border)',
  },
  toolbarTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
  },
  trackNameLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-text)',
  },
  segCount: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
  },
  resetBtn: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    cursor: 'pointer',
  },
  body: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  canvasContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    touchAction: 'none',
  },
  canvas: {
    display: 'block',
    width: '100%',
    height: '100%',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: 400,
  },
  emptyTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 18,
    color: 'var(--color-text)',
    letterSpacing: 1,
    marginBottom: 12,
  },
  emptyText: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
  },
  bottomBar: {
    padding: '8px 24px',
    background: 'var(--color-surface)',
    borderTop: '1px solid var(--color-border)',
    textAlign: 'center',
  },
  hint: {
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    color: 'var(--color-text-dim)',
  },
};
